import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Grid, Typography, CircularProgress, Box, Button } from '@mui/material'
import { useFormikContext } from 'formik'
import { User } from 'src/api'
import { DialogNames, UserRoles } from 'src/enums'
import { useUserListRequest } from 'src/hooks/api'
import { useDialog } from 'src/hooks/ui'
import { formatPhone } from 'src/utils/formatters'
import { TextField, PhoneInput } from 'src/components/ui'
import { ConfirmOwnerRoleDowngradeModal } from './confirmOwnerRoleDowngradeModal'
import { ContractorNewState } from './new'

type OwnerLookupStatus =
  | 'initial'
  | 'searching'
  | 'admin_error'
  | 'downgrade_confirmed'
  | 'view_only_found'
  | 'new_user'

interface DowngradeInfo {
  roleName: string
  contractorName: string
}

function classifyFoundUser(user: User): 'admin' | 'owner_pm_foreman' | 'view_only' {
  if (user.role?.isAdmin) return 'admin'

  const ucs = user.userContractors || []
  const hasHighRole = ucs.some(
    (uc) => uc.role?.name && [UserRoles.Owner, UserRoles.PM, UserRoles.Foreman].includes(uc.role.name as UserRoles)
  )
  if (hasHighRole) return 'owner_pm_foreman'

  return 'view_only'
}

function getDowngradeInfo(user: User): DowngradeInfo {
  const ucs = user.userContractors || []
  const highRoleUc = ucs.find(
    (uc) => uc.role?.name && [UserRoles.Owner, UserRoles.PM, UserRoles.Foreman].includes(uc.role.name as UserRoles)
  )
  return {
    roleName: highRoleUc?.role?.displayName || highRoleUc?.role?.name || 'member',
    contractorName: highRoleUc?.contractor?.name || 'unknown',
  }
}

interface OwnerUserSectionProps {
  isLoading: boolean
  onSubmitDisabledChange: (disabled: boolean) => void
}

export const OwnerUserSection: FC<OwnerUserSectionProps> = ({ isLoading, onSubmitDisabledChange }) => {
  const { values, setFieldValue } = useFormikContext<ContractorNewState>()
  const [foundUser, setFoundUser] = useState<User | null>(null)
  const [downgradeInfo, setDowngradeInfo] = useState<DowngradeInfo | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const prevPhoneRef = useRef<string | undefined>(undefined)

  const { openDialog: openDowngradeDialog, closeDialog: closeDowngradeDialog } = useDialog(
    DialogNames.ConfirmOwnerRoleDowngrade
  )

  const formattedPhone = useMemo(() => formatPhone(values.ownerPhone), [values.ownerPhone])
  const hasValidPhone = useMemo(() => {
    return !!formattedPhone && formattedPhone.length >= 10
  }, [formattedPhone])

  // Initialize search state: if phone is pre-populated on mount, search immediately
  const initialPhone = useMemo(() => {
    const phone = formatPhone(values.ownerPhone)
    return phone && phone.length >= 10 ? phone : undefined
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const [lookupStatus, setLookupStatus] = useState<OwnerLookupStatus>(initialPhone ? 'searching' : 'initial')
  const [searchPhone, setSearchPhone] = useState<string | undefined>(initialPhone)

  const { data: searchResult, isFetching } = useUserListRequest({
    params: {
      search: searchPhone,
      limit: 5,
      offset: 0,
    },
    options: {
      enabled: !!searchPhone,
    },
  })

  // Debounced phone search trigger — only fires on phone changes after mount
  useEffect(() => {
    // Skip the initial render since we handled it via useState initialization
    if (prevPhoneRef.current === undefined) {
      prevPhoneRef.current = formattedPhone
      return
    }

    // Phone hasn't actually changed (e.g. reformatting by PhoneInput)
    if (prevPhoneRef.current === formattedPhone) {
      return
    }

    prevPhoneRef.current = formattedPhone

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    if (!hasValidPhone) {
      if (lookupStatus !== 'initial') {
        setLookupStatus('initial')
        setFoundUser(null)
        setDowngradeInfo(null)
        setSearchPhone(undefined)
      }
      return
    }

    debounceRef.current = setTimeout(() => {
      setLookupStatus('searching')
      setSearchPhone(formattedPhone)
    }, 800)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [formattedPhone, hasValidPhone]) // eslint-disable-line react-hooks/exhaustive-deps

  // Process search results
  useEffect(() => {
    if (lookupStatus !== 'searching' || isFetching) return
    if (!searchResult) return

    const users = searchResult.rows || []

    // Find exact phone match
    const matchedUser = users.find((u) => {
      const userPhone = formatPhone(u.phone)
      return userPhone === formattedPhone
    })

    if (!matchedUser) {
      setLookupStatus('new_user')
      setFoundUser(null)
      setFieldValue('ownerName', '')
      setFieldValue('ownerEmail', '')
      return
    }

    setFoundUser(matchedUser)
    const classification = classifyFoundUser(matchedUser)

    if (classification === 'admin') {
      setLookupStatus('admin_error')
    } else if (classification === 'owner_pm_foreman') {
      const info = getDowngradeInfo(matchedUser)
      setDowngradeInfo(info)
      openDowngradeDialog()
    } else {
      setLookupStatus('view_only_found')
      setFieldValue('ownerName', matchedUser.name || '')
      setFieldValue('ownerEmail', matchedUser.email || '')
    }
  }, [searchResult, isFetching, lookupStatus]) // eslint-disable-line react-hooks/exhaustive-deps

  // Notify parent about submit disabled state
  useEffect(() => {
    const isDisabled = lookupStatus === 'admin_error' || lookupStatus === 'initial' || lookupStatus === 'searching'
    onSubmitDisabledChange(isDisabled)
  }, [lookupStatus, onSubmitDisabledChange])

  const handleDowngradeConfirm = useCallback(() => {
    closeDowngradeDialog()
    setLookupStatus('downgrade_confirmed')
    if (foundUser) {
      setFieldValue('ownerName', foundUser.name || '')
      setFieldValue('ownerEmail', foundUser.email || '')
    }
  }, [closeDowngradeDialog, foundUser, setFieldValue])

  const handleDowngradeCancel = useCallback(() => {
    closeDowngradeDialog()
    setLookupStatus('initial')
    setFoundUser(null)
    setDowngradeInfo(null)
    setSearchPhone(undefined)
    setFieldValue('ownerPhone', '')
    setFieldValue('ownerName', '')
    setFieldValue('ownerEmail', '')
  }, [closeDowngradeDialog, setFieldValue])

  const showOwnerFields = lookupStatus === 'new_user' || lookupStatus === 'view_only_found' || lookupStatus === 'downgrade_confirmed'

  return (
    <>
      <Typography variant="h4">Contractor owner user</Typography>
      <Grid container spacing={1} mt={2}>
        <Grid item xs={12}>
          <PhoneInput name="ownerPhone" label="Phone number" required />
        </Grid>

        {lookupStatus === 'searching' && (
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Searching for user...
              </Typography>
            </Box>
          </Grid>
        )}

        {lookupStatus === 'admin_error' && (
          <Grid item xs={12}>
            <Typography color="error">
              This is a system admin user. You can&apos;t make them an owner.
            </Typography>
          </Grid>
        )}

        {lookupStatus === 'downgrade_confirmed' && downgradeInfo && (
          <Grid item xs={12}>
            <Typography color="error">
              This user is {downgradeInfo.roleName} of the {downgradeInfo.contractorName} contractor. Their current role will be downgraded to the view-only.
            </Typography>
          </Grid>
        )}

        {lookupStatus === 'view_only_found' && (
          <Grid item xs={12}>
            <Typography color="text.primary">
              This user is already exist in the system as a view-only member.
            </Typography>
          </Grid>
        )}

        {showOwnerFields && (
          <>
            <Grid item xs={12}>
              <TextField
                name="ownerName"
                label="Name"
                required
                TextFieldProps={{ placeholder: 'Name' }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="ownerEmail"
                label="Email"
                required
                TextFieldProps={{ placeholder: 'Email' }}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Typography>
            Owner will receive an invitation. Other users can not login,
            unless owner signed the agreement upon login
          </Typography>
        </Grid>
        <Grid item xs={12} sx={{ display: { xs: 'none', sm: 'block' } }}>
          <Box pt={2}>
            <Button
              size="large"
              variant="contained"
              color="primary"
              type="submit"
              disabled={isLoading || lookupStatus === 'admin_error' || lookupStatus === 'initial' || lookupStatus === 'searching'}
            >
              {isLoading ? <CircularProgress color="info" size={26} /> : 'Create contractor and invite owner'}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <ConfirmOwnerRoleDowngradeModal
        roleName={downgradeInfo?.roleName || ''}
        contractorName={downgradeInfo?.contractorName || ''}
        onConfirm={handleDowngradeConfirm}
        onCancel={handleDowngradeCancel}
      />
    </>
  )
}
